import React, { useEffect, useState, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import IconByName from "@/components/ds/IconByName";

export type SkillMode = "enabled" | "prompt" | "disabled";

export interface SkillDef {
  name: string;
  label: string;
  group?: string;
  description: string;
  /** Write-tier: creates/updates records. Requires EnableAdvancedAiSkills. */
  write?: boolean;
  /** Destructive-tier: cancels orders, issues refunds, deletes data. Requires EnableDestructiveAiSkills. */
  destructive?: boolean;
  /**
   * Shown when this skill is selected on a node that may run on IVR or SMS.
   * Use for skills that send links, display UI, or require email — things that
   * don't work on voice/text channels without a companion workflow section.
   */
  channelWarning?: string;
}

interface SkillOverride {
  skillName: string;
  mode: SkillMode;
}

interface CatalogEntry {
  name: string;
  subscriptionFlag: string | null;
  /** Whether this SPECIFIC skill's SubscriptionFlag is actually true on this site's real
   *  subscription right now (server-computed via the same gate every skill call is checked
   *  against at runtime) — never assumed true just because the integration is connected.
   *  Two skills on the same integration can require different flags. */
  included: boolean;
}

/** Turns a flag like "EnableCommerceSkills" into "Commerce Skills" for display. */
function humanizeFlag(flag: string | null | undefined): string {
  if (!flag) return "a higher plan";
  return flag.replace(/^Enable/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
}

interface Props {
  integration: string;
  skills: SkillDef[];
  className?: string;
  /** When provided, overrides are scoped to this bot (AIConfiguration ID) instead of site-wide. */
  botId?: number;
}

const MODE_CONFIG: Record<SkillMode, { label: string; color: string; bg: string; border: string; icon: string }> = {
  enabled:  { label: "Enabled",    color: "text-green-700", bg: "bg-green-50", border: "border-green-300", icon: "Check" },
  prompt:   { label: "Ask first",  color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-300", icon: "HelpCircle" },
  disabled: { label: "Disabled",   color: "text-red-700",   bg: "bg-red-50",   border: "border-red-300",   icon: "X" },
};

export default function SkillControlsPanel({ integration, skills, className = "", botId }: Props) {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [overrides, setOverrides] = useState<Record<string, SkillMode>>({});
  const [catalogByName, setCatalogByName] = useState<Record<string, CatalogEntry>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const getEndpoint = () =>
    botId != null
      ? `BotSkillOverrides/${botId}`
      : `SiteSkillOverrides?integration=${integration}`;

  const getPutEndpoint = () =>
    botId != null ? `BotSkillOverrides/${botId}` : "SiteSkillOverrides";

  useEffect(() => {
    async function load() {
      try {
        const resp = await api.messaging.get(getEndpoint());
        const data: SkillOverride[] = await resp.json();
        const map: Record<string, SkillMode> = {};
        for (const row of Array.isArray(data) ? data : []) {
          map[row.skillName] = row.mode as SkillMode;
        }
        setOverrides(map);
        // Auto-expand if any skills are already customized
        if (Object.keys(map).length > 0) setExpanded(true);
      } catch {
        setOverrides({});
      }
    }
    load();

    // Real per-skill entitlement, server-computed from this site's actual subscription —
    // never assumed from "the integration is connected, so every skill must be included".
    //
    // Deliberately fetches the FULL catalog (no ?integration= filter) and matches entries by
    // skill NAME below, rather than filtering server-side by the `integration` prop. The
    // catalog's server-side filter compares against IWorkflowSkill.Category, but several
    // integration pages pass a prop that doesn't match their skills' Category 1:1 (e.g.
    // "dynamics" vs Category "Dynamics365", "quickbooks" vs "Accounting", "sap" vs "ERP",
    // "servicenow" vs "ITSM", "vast" vs "AutoService") — filtering server-side would silently
    // return zero rows for those integrations and revert to the pre-fix no-badges behavior.
    // Matching by exact ToolName here is immune to that mismatch.
    async function loadCatalog() {
      try {
        const resp = await api.messaging.get(`SiteSkillOverrides/catalog`);
        const data: CatalogEntry[] = await resp.json();
        const map: Record<string, CatalogEntry> = {};
        for (const entry of Array.isArray(data) ? data : []) {
          map[entry.name] = entry;
        }
        setCatalogByName(map);
      } catch (err) {
        console.error("SkillControlsPanel: failed to load skill catalog/entitlement info", err);
        setCatalogByName({});
      }
    }
    loadCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration, botId]);

  const getMode = (skillName: string): SkillMode =>
    overrides[skillName] ?? "enabled";

  const saveOverride = useCallback(
    async (skillName: string, newMode: SkillMode, prev: Record<string, SkillMode>) => {
      setSaving(true);
      setSaved(false);
      try {
        await api.messaging.put(getPutEndpoint(), {
          json: botId != null
            ? [{ skillName, mode: newMode }]
            : [{ integration, skillName, mode: newMode }],
        });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } catch {
        setOverrides(prev); // revert on failure
      } finally {
        setSaving(false);
      }
    },
    [integration, api]
  );

  const handleModeChange = useCallback(
    (skillName: string, newMode: SkillMode) => {
      const prev = overrides;
      const updated = { ...overrides, [skillName]: newMode };
      if (newMode === "enabled") delete updated[skillName];
      setOverrides(updated);
      saveOverride(skillName, newMode, prev);
    },
    [overrides, saveOverride]
  );

  // One-click: disable all destructive skills
  const handleDisableDestructive = useCallback(async () => {
    const destructiveSkills = skills.filter(s => s.destructive);
    if (destructiveSkills.length === 0) return;
    const prev = overrides;
    const updated = { ...overrides };
    for (const s of destructiveSkills) updated[s.name] = "disabled";
    setOverrides(updated);
    setSaving(true);
    try {
      await api.messaging.put(getPutEndpoint(), {
        json: botId != null
          ? destructiveSkills.map(s => ({ skillName: s.name, mode: "disabled" }))
          : destructiveSkills.map(s => ({ integration, skillName: s.name, mode: "disabled" })),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setOverrides(prev);
    } finally {
      setSaving(false);
    }
  }, [skills, overrides, integration, api]);

  const customizedCount = Object.keys(overrides).length;
  const destructiveSkills = skills.filter(s => s.destructive);
  const allDestructiveDisabled = destructiveSkills.every(s => overrides[s.name] === "disabled");

  const isPlanBlocked = (skill: SkillDef): { message: string; featureName: string; flagName: string } | null => {
    const catalogEntry = catalogByName[skill.name];
    // Server said this specific skill's flag isn't on for this site. Checked first — a skill
    // can be entitlement-blocked independent of (and before) the write/destructive tier gates
    // below, e.g. a Read-tier skill still gated behind a package-specific flag.
    if (catalogEntry && catalogEntry.included === false) {
      const featureName = humanizeFlag(catalogEntry.subscriptionFlag);
      return {
        // Deliberately doesn't claim WHY — the server-side gate also excludes a skill when its
        // integration isn't fully configured yet, not only when the plan lacks the flag. Both
        // reasons resolve to "not usable right now"; the flag name below gives support enough
        // to diagnose which one it actually is.
        message: `This skill isn't currently available on your account.`,
        featureName,
        flagName: catalogEntry.subscriptionFlag ?? "Unknown"
      };
    }
    if (skill.destructive && !subscription?.enableDestructiveAiSkills)
      return {
        message: "This skill requires the Destructive AI Skills add-on — cancel orders, issue refunds, and delete records.",
        featureName: "Destructive AI Skills",
        flagName: "EnableDestructiveAiSkills"
      };
    if (skill.write && !subscription?.enableAdvancedAiSkills)
      return {
        message: "This skill requires the Advanced AI Skills add-on — create records, update orders, and assign tasks.",
        featureName: "Advanced AI Skills",
        flagName: "EnableAdvancedAiSkills"
      };
    return null;
  };

  const catalogLoaded = Object.keys(catalogByName).length > 0;
  const includedCount = skills.filter(s => catalogByName[s.name]?.included !== false).length;
  const lockedCount = skills.length - includedCount;

  const groups = Array.from(new Set(skills.map(s => s.group ?? "General")));

  const toggleGroup = (g: string) =>
    setOpenGroups(prev => ({ ...prev, [g]: !prev[g] }));

  const isGroupOpen = (g: string) =>
    openGroups[g] !== undefined ? openGroups[g] : true; // default open

  return (
    <div className={`rounded-xl border border-zinc-200 overflow-hidden ${className}`}>
      {/* ── Collapsed header (always visible) ── */}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-4 bg-zinc-50 hover:bg-zinc-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-zinc-800">Skill Controls</span>
          <span className="text-xs text-zinc-400">{skills.length} skills</span>
          {catalogLoaded && lockedCount > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
              {includedCount} available now · {lockedCount} not currently available
            </span>
          )}
          {customizedCount > 0 && (
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              {customizedCount} customized
            </span>
          )}
          {saving && <span className="text-xs text-zinc-400">Saving…</span>}
          {saved && <span className="text-xs text-green-600 font-medium">Saved</span>}
        </div>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform flex-shrink-0 ${expanded ? "rotate-180" : ""}`}
          viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {/* ── Expanded content ── */}
      {expanded && (
        <div className="px-5 pb-5 pt-4 space-y-5 bg-white">
          {/* Legend + quick action */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3 text-xs text-zinc-600">
              {(Object.entries(MODE_CONFIG) as [SkillMode, typeof MODE_CONFIG["enabled"]][]).map(([mode, cfg]) => (
                <div key={mode} className="flex items-center gap-1.5">
                  <span className={`w-5 h-5 rounded flex items-center justify-center ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                    <IconByName name={cfg.icon} className="w-3 h-3" />
                  </span>
                  <span className="text-zinc-500">{cfg.label}</span>
                </div>
              ))}
            </div>
            {destructiveSkills.length > 0 && !allDestructiveDisabled && (
              <button
                type="button"
                onClick={handleDisableDestructive}
                className="text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg px-3 py-1.5 transition-colors flex-shrink-0"
              >
                Disable all destructive skills
              </button>
            )}
          </div>

          {/* Customized skills callout */}
          {customizedCount > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Customized skills</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(overrides).map(([skillName, mode]) => {
                  const skill = skills.find(s => s.name === skillName);
                  const cfg = MODE_CONFIG[mode];
                  return (
                    <span key={skillName} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                      <IconByName name={cfg.icon} className="w-3 h-3" /> {skill?.label ?? skillName}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Groups */}
          {groups.map(group => {
            // Available skills first, then locked ones — within each bucket, ascending by tier
            // (read < write < destructive) so a site sees what it CAN use before what it can't,
            // and upgrade prompts climb in the same order the packages/billing tiers do.
            const tierRank = (s: SkillDef) => (s.destructive ? 2 : s.write ? 1 : 0);
            const groupSkills = skills
              .filter(s => (s.group ?? "General") === group)
              .slice()
              .sort((a, b) => {
                const aIncluded = catalogByName[a.name]?.included !== false;
                const bIncluded = catalogByName[b.name]?.included !== false;
                if (aIncluded !== bIncluded) return aIncluded ? -1 : 1;
                return tierRank(a) - tierRank(b);
              });
            const open = isGroupOpen(group);
            const groupCustomized = groupSkills.filter(s => overrides[s.name]).length;
            return (
              <div key={group} className="rounded-lg border border-zinc-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => toggleGroup(group)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-50 hover:bg-zinc-100 text-left transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-zinc-600 uppercase tracking-wider">{group}</span>
                    <span className="text-xs text-zinc-400">{groupSkills.length}</span>
                    {groupCustomized > 0 && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        {groupCustomized} changed
                      </span>
                    )}
                  </div>
                  <svg
                    className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
                    viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"
                  >
                    <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                  </svg>
                </button>

                {open && (
                  <div className="divide-y divide-zinc-100">
                    {groupSkills.map(skill => {
                      const mode = getMode(skill.name);
                      const cfg = MODE_CONFIG[mode];
                      const isCustomized = !!overrides[skill.name];
                      const planBlock = isPlanBlocked(skill);
                      return (
                        <div
                          key={skill.name}
                          className={`flex items-center justify-between gap-4 px-4 py-2.5 transition-colors ${
                            planBlock ? "bg-violet-50/30" : mode === "disabled" ? "bg-zinc-50/70" : isCustomized ? "bg-amber-50/40" : "bg-white"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-zinc-800">{skill.label}</span>
                              {skill.destructive && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                  Destructive
                                </span>
                              )}
                              {skill.write && !skill.destructive && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                  Write
                                </span>
                              )}
                              {planBlock && (
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                                  Upgrade to {planBlock.featureName}
                                </span>
                              )}
                              <span className="text-[10px] font-mono text-zinc-300">{skill.name}</span>
                            </div>
                            {planBlock ? (
                              <p className="text-xs text-zinc-500 mt-0.5 leading-snug">
                                {planBlock.message}{" "}
                                <span className="text-zinc-400">
                                  Contact support with: <span className="font-mono">{planBlock.flagName}</span>
                                </span>
                              </p>
                            ) : (
                              <p className="text-xs text-zinc-400 mt-0.5 leading-snug">{skill.description}</p>
                            )}
                          </div>

                          {/* 3-way toggle — disabled when plan blocks the skill */}
                          <div className={`flex items-center rounded-md border overflow-hidden flex-shrink-0 text-xs font-bold shadow-sm ${planBlock ? "border-zinc-100 cursor-not-allowed" : "border-zinc-200"}`}>
                            {(["enabled", "prompt", "disabled"] as SkillMode[]).map(m => {
                              const c = MODE_CONFIG[m];
                              const active = mode === m;
                              return (
                                <button
                                  key={m}
                                  type="button"
                                  onClick={() => !planBlock && handleModeChange(skill.name, m)}
                                  title={planBlock ? `Upgrade to ${planBlock.featureName} to configure this skill` : c.label}
                                  disabled={!!planBlock}
                                  className={`px-2.5 py-1.5 transition-colors ${
                                    planBlock
                                      ? "bg-zinc-50 text-zinc-200 cursor-not-allowed"
                                      : active
                                        ? `${c.bg} ${c.color}`
                                        : "bg-white text-zinc-300 hover:bg-zinc-50 hover:text-zinc-500"
                                  }`}
                                >
                                  <IconByName name={c.icon} className="w-3.5 h-3.5" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
